import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import api, { errMsg } from "../api";
import { useAuth } from "../AuthContext";
import { t } from "../i18n";
import TaskCard from "../components/TaskCard";
import TaskDetailDialog from "../components/TaskDetailDialog";
import NewTaskDialog from "../components/NewTaskDialog";
import { Button } from "../components/ui/button";
import { Plus, Users as UsersIcon, ListChecks, Clock, Activity, CheckCircle2, Settings2 } from "lucide-react";
import { Badge } from "../components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "../components/ui/dialog";
import { toast } from "sonner";

const COLUMNS = [
    { key: "created", accent: "bg-slate-100 text-slate-700 border-slate-200" },
    { key: "taken", accent: "bg-blue-100 text-blue-800 border-blue-200" },
    { key: "working", accent: "bg-amber-100 text-amber-800 border-amber-200" },
    { key: "completed", accent: "bg-emerald-100 text-emerald-800 border-emerald-200" },
];

export default function SpaceViewPage() {
    const { spaceId } = useParams();
    const { user, lang } = useAuth();
    const [space, setSpace] = useState(null);
    const [tasks, setTasks] = useState([]);
    const [users, setUsers] = useState([]);
    const [allSpaces, setAllSpaces] = useState([]);
    const [openTaskId, setOpenTaskId] = useState(null);
    const [newTaskOpen, setNewTaskOpen] = useState(false);
    const [manageOpen, setManageOpen] = useState(false);

    const load = useCallback(async () => {
        const [sp, tk, u] = await Promise.all([
            api.get("/spaces"),
            api.get(`/tasks?space_id=${spaceId}`),
            api.get("/users"),
        ]);
        setAllSpaces(sp.data);
        setSpace(sp.data.find((s) => s.id === spaceId));
        setTasks(tk.data);
        setUsers(u.data);
    }, [spaceId]);

    useEffect(() => { load(); }, [load]);

    // Refresh when a task changes from the global dialog
    useEffect(() => {
        const h = () => load();
        window.addEventListener("taskflow:task-changed", h);
        return () => window.removeEventListener("taskflow:task-changed", h);
    }, [load]);

    const handleDragOver = (e) => e.preventDefault();
    const handleDrop = async (e, status) => {
        e.preventDefault();
        const taskId = e.dataTransfer.getData("task-id");
        if (!taskId) return;
        try {
            await api.patch(`/tasks/${taskId}`, { status });
            load();
        } catch {}
    };

    const members = users.filter((u) => (space?.member_ids || []).includes(u.id));

    if (!space) {
        return <div className="p-8 text-neutral-500" data-testid="space-loading">Loading...</div>;
    }

    return (
        <div className="p-4 sm:p-6 md:p-8 space-y-6" data-testid={`space-view-${spaceId}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="flex items-center gap-2 mb-1">
                        <span className="w-2.5 h-2.5 rounded-sm" style={{ background: space.color }} />
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-neutral-500">{t(lang, "spaces")}</p>
                    </div>
                    <h1 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-neutral-900">{space.name}</h1>
                    {space.description && <p className="text-sm text-neutral-600 mt-1">{space.description}</p>}
                    <div className="flex items-center gap-3 mt-3 text-xs text-neutral-500">
                        <span className="flex items-center gap-1"><UsersIcon className="w-3 h-3" />{members.length} {t(lang, "members")}</span>
                        <span>·</span>
                        <span>{tasks.length} {t(lang, "tasks")}</span>
                    </div>
                </div>
                <div className="flex gap-2">
                    {user?.role === "admin" && (
                        <Button variant="outline" onClick={() => setManageOpen(true)} data-testid="manage-space-btn">
                            <Settings2 className="w-4 h-4 mr-1" />
                            {lang === "tr" ? "Yönet" : "Manage"}
                        </Button>
                    )}
                    <Button onClick={() => setNewTaskOpen(true)} className="bg-neutral-900 hover:bg-neutral-800" data-testid="space-new-task-btn">
                        <Plus className="w-4 h-4 mr-1" />{t(lang, "newTask")}
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="space-kanban">
                {COLUMNS.map((col) => {
                    const colTasks = tasks.filter((tk) => tk.status === col.key);
                    return (
                        <div
                            key={col.key}
                            onDragOver={handleDragOver}
                            onDrop={(e) => handleDrop(e, col.key)}
                            data-testid={`space-column-${col.key}`}
                            className="flex flex-col gap-2.5 rounded-lg border border-neutral-200 bg-neutral-50/70 p-3 min-h-[60vh]"
                        >
                            <div className="flex items-center justify-between mb-1">
                                <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-md border ${col.accent}`}>
                                    {t(lang, col.key)}
                                </span>
                                <span className="text-xs text-neutral-500">{colTasks.length}</span>
                            </div>
                            <div className="space-y-2">
                                {colTasks.map((tk) => (
                                    <TaskCard
                                        key={tk.id}
                                        task={tk}
                                        users={users}
                                        onOpen={() => setOpenTaskId(tk.id)}
                                        lang={lang}
                                        draggable
                                        onDragStart={(e) => e.dataTransfer.setData("task-id", tk.id)}
                                    />
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>

            <NewTaskDialog
                open={newTaskOpen}
                onClose={() => setNewTaskOpen(false)}
                onCreated={load}
                spaces={allSpaces}
                users={users}
                defaultSpaceId={spaceId}
            />
            <TaskDetailDialog
                taskId={openTaskId}
                open={!!openTaskId}
                onClose={() => setOpenTaskId(null)}
                onChange={load}
                allUsers={users}
                spaces={allSpaces}
            />
            <ManageSpaceDialog
                open={manageOpen}
                onClose={() => setManageOpen(false)}
                space={space}
                users={users}
                onChange={load}
                lang={lang}
            />
        </div>
    );
}

function ManageSpaceDialog({ open, onClose, space, users, onChange, lang }) {
    const [selected, setSelected] = useState([]);
    useEffect(() => {
        if (space) setSelected(space.member_ids || []);
    }, [space, open]);

    const save = async () => {
        try {
            await api.patch(`/spaces/${space.id}`, { member_ids: selected });
            toast.success(lang === "tr" ? "Güncellendi" : "Updated");
            onChange();
            onClose();
        } catch (e) {
            toast.error(errMsg(e, "Error"));
        }
    };

    const removeSpace = async () => {
        if (!window.confirm(t(lang, "confirmDeleteSpace"))) return;
        try {
            await api.delete(`/spaces/${space.id}`);
            toast.success(lang === "tr" ? "Silindi" : "Deleted");
            window.location.href = "/";
        } catch (e) {
            toast.error(errMsg(e, "Error"));
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-md" data-testid="manage-space-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display">{space?.name}</DialogTitle>
                </DialogHeader>
                <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-neutral-700 mb-2">{t(lang, "spaceMembers")}</p>
                    <div className="space-y-1 max-h-64 overflow-y-auto">
                        {users.map((u) => {
                            const checked = selected.includes(u.id);
                            return (
                                <label key={u.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-neutral-50 cursor-pointer" data-testid={`member-toggle-${u.id}`}>
                                    <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={(e) => setSelected((prev) => e.target.checked ? [...prev, u.id] : prev.filter((x) => x !== u.id))}
                                    />
                                    <div className="w-7 h-7 rounded-full bg-neutral-900 text-white text-[10px] font-semibold flex items-center justify-center">
                                        {u.name?.slice(0, 2)?.toUpperCase()}
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-sm font-medium">{u.name}</p>
                                        <p className="text-[11px] text-neutral-500">{u.email}</p>
                                    </div>
                                    <Badge variant="outline" className="text-[10px] uppercase">{u.role}</Badge>
                                </label>
                            );
                        })}
                    </div>
                </div>
                <DialogFooter className="flex !justify-between">
                    <Button variant="destructive" onClick={removeSpace} data-testid="delete-space-btn">{t(lang, "deleteSpace")}</Button>
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={onClose}>{t(lang, "cancel")}</Button>
                        <Button onClick={save} className="bg-neutral-900 hover:bg-neutral-800" data-testid="save-members-btn">{t(lang, "save")}</Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
